import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaClock, FaUtensils, FaAppleAlt, FaWater } from 'react-icons/fa';
import Header from '../../Header';
import '../../scss/parentArticleView.scss';

// Sample article content for the nutrition article
const NUTRITION_ARTICLE = {
  title: "Nutrition Tips for Young Athletes Before a Game",
  author: "Sarah Johnson",
  authorRole: "Sports Nutritionist & Football Parent",
  publishDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }),
  readTime: "5 min read",
  heroImage: "https://images.unsplash.com/photo-1494390248081-4e521a5940db?auto=format&fit=crop&w=1350&h=400&q=80",
  content: [
    {
      type: "intro",
      text: "As a mum of two football-mad children, I've learned through trial and error what works best for pre-game nutrition. Getting the right fuel into active kids isn't just about performance - it's about helping them enjoy the game without feeling sluggish or hungry. Here's what I've learned about feeding young athletes before matches."
    },
    {
      type: "section",
      title: "Timing is Everything",
      text: "The biggest game-changer for us has been getting the timing right. For morning matches, I make sure the kids eat a proper breakfast at least 2 hours before kick-off. For afternoon games, lunch should be 3-4 hours before, with a light snack about an hour before play.",
      image: "",
      imageCaption: "Planning meals around game schedules is key to proper fueling"
    },
    {
      type: "section",
      title: "The Pre-Game Meal Blueprint",
      text: "After many Saturday mornings of trial and error, I've found that a winning pre-game meal includes a good balance of complex carbohydrates, moderate protein, and low fat. For my kids, this often looks like porridge with banana and honey, wholegrain toast with scrambled eggs, or pasta with lean chicken. Keep portions moderate - playing with a too-full stomach leads to discomfort and sluggishness."
    },
    {
      type: "tip-list",
      title: "Pre-Match Food Ideas That Work",
      tips: [
        "Porridge with sliced banana and a light drizzle of honey",
        "Wholegrain toast with scrambled egg and a small glass of diluted fruit juice",
        "Pasta with tomato sauce and small amount of chicken or tuna",
        "Baked potato with beans (small portion)",
        "Sandwich with lean meat and salad on wholegrain bread"
      ]
    },
    {
      type: "section",
      title: "Hydration: The Secret Weapon",
      text: "I've noticed that proper hydration makes a massive difference to my children's energy levels and performance. They start drinking water the day before the match and sip regularly throughout the pre-game period. Avoid sugary drinks which can cause energy spikes and crashes. If it's a particularly hot day, I add a pinch of salt and a squeeze of lemon to their water bottles for natural electrolytes.",
      image: "https://images.unsplash.com/photo-1523362628745-0c100150b504?auto=format&fit=crop&w=1350&h=400&q=80",
      imageCaption: "Water is still the best option for most young players"
    },
    {
      type: "warning",
      title: "Foods to Avoid Before the Match",
      text: "Through unfortunate experience (and some mid-game tummy complaints), we've learned to avoid these foods before playing:",
      items: [
        "Sugary cereals or sweets - causes energy spikes and crashes",
        "Fatty foods like chips or fried breakfast - slow digestion and can cause stomach discomfort",
        "Large amounts of dairy - can cause bloating in some children",
        "Unfamiliar foods - match day isn't the time to try new things",
        "High-fiber foods - can cause digestive discomfort during play"
      ]
    },
    {
      type: "section",
      title: "Emergency Snacks",
      text: "Sometimes matches get delayed or training runs longer than expected. I always pack emergency snacks that provide quick, digestible energy like bananas, rice cakes with a thin spread of peanut butter, or homemade energy balls. These can be eaten 30-60 minutes before play if needed.",
      image: "",
      imageCaption: "Having emergency snacks ready can save the day when schedules change"
    },
    {
      type: "recipe",
      title: "Our Match Day Energy Balls Recipe",
      ingredients: [
        "1 cup oats",
        "2 tbsp honey or maple syrup",
        "1/3 cup smooth peanut butter",
        "1/4 cup dried fruit (raisins or chopped dates)",
        "1 tbsp chia seeds (optional)",
        "1 tsp vanilla extract"
      ],
      instructions: "Mix all ingredients in a bowl until well combined. Roll into small balls (about the size of a walnut). Store in the fridge for up to a week. Perfect for a quick energy boost 30-60 minutes before a game!"
    },
    {
      type: "section",
      title: "Listen to Your Child",
      text: "Finally, the most important lesson I've learned is that every child is different. What works for one might not work for another. Pay attention to how your child feels after different meals and snacks. If they complain of feeling sluggish or having stomach discomfort, adjust accordingly. The goal is to find what helps them feel energized and comfortable during play."
    },
    {
      type: "conclusion",
      text: "Remember, we're feeding children who play sports, not professional athletes. Keep it simple, nutritious, and enjoyable. The right nutrition approach can help them perform better, but more importantly, it helps them enjoy the game more - and that's what youth sports should be all about."
    }
  ]
};

// Mock data for other articles
const OTHER_ARTICLES = [
  {
    id: 1,
    title: "Supporting Your Child's Development Off the Pitch",
    excerpt: "Learn how simple home activities can significantly improve your child's football skills and confidence.",
    image: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    slug: "supporting-development"
  },
  {
    id: 2,
    title: "Building Resilience: Helping Players Bounce Back From Defeats",
    excerpt: "Discover effective ways to help your young footballer develop mental toughness and overcome setbacks.",
    image: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=600&q=80",
    slug: "building-resilience"
  }
];

const ParentArticleView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  
  // For this example, we're only showing the nutrition article regardless of the slug
  const article = NUTRITION_ARTICLE;
  
  const goBack = () => {
    navigate(-1);
  };
  
  const renderContent = (contentItem, index) => {
    switch(contentItem.type) {
      case 'intro':
        return (
          <div className="article-intro" key={index}>
            <p>{contentItem.text}</p>
          </div>
        );
        
      case 'section':
        return (
          <div className="article-section" key={index}>
            {contentItem.title && <h3>{contentItem.title}</h3>}
            <p>{contentItem.text}</p>
            {contentItem.image && contentItem.image.trim() !== "" && (
              <div className="article-image-container">
                <img src={contentItem.image} alt={contentItem.title} className="article-section-image" />
                {contentItem.imageCaption && <p className="image-caption">{contentItem.imageCaption}</p>}
              </div>
            )}
          </div>
        );
        
      case 'tip-list':
        return (
          <div className="article-tips" key={index}>
            <h3>{contentItem.title}</h3>
            <ul>
              {contentItem.tips.map((tip, i) => (
                <li key={i}>
                  <FaUtensils className="tip-icon" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        );
        
      case 'warning':
        return (
          <div className="article-warning" key={index}>
            <h3>{contentItem.title}</h3>
            <p>{contentItem.text}</p>
            <ul>
              {contentItem.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        );
        
      case 'recipe':
        return (
          <div className="article-recipe" key={index}>
            <h3>{contentItem.title}</h3>
            <div className="recipe-ingredients">
              <h4><FaAppleAlt /> Ingredients</h4>
              <ul>
                {contentItem.ingredients.map((ingredient, i) => (
                  <li key={i}>{ingredient}</li>
                ))}
              </ul>
            </div>
            <div className="recipe-instructions">
              <h4>Instructions</h4>
              <p>{contentItem.instructions}</p>
            </div>
          </div>
        );
        
      case 'conclusion':
        return (
          <div className="article-conclusion" key={index}>
            <p>{contentItem.text}</p>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="parent-article-view">
      <Header />
      
      <div className="article-container">
        <button className="back-button" onClick={goBack}>
          <FaArrowLeft /> Back to articles
        </button>
        
        <div className="article-hero">
          <img src={article.heroImage} alt={article.title} className="hero-image" />
          <div className="article-hero-overlay">
            <h1>{article.title}</h1>
          </div>
        </div>
        
        <div className="article-meta">
          <div className="author-info">
            <span className="author-name">By {article.author}</span>
            <span className="author-role">{article.authorRole}</span>
          </div>
          <div className="article-details">
            <span className="publish-date">{article.publishDate}</span>
            <span className="read-time"><FaClock /> {article.readTime}</span>
          </div>
        </div>
        
        <div className="article-body">
          {article.content.map(renderContent)}
        </div>
        

      </div>
    </div>
  );
};

export default ParentArticleView; 